import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateMaintenanceHistoryInput,
  CreateMaintenancePartsInput,
  DeleteMaintenanceHistoryInput,
  DeleteMaintenancePartsInput,
  GetMaintenanceHistoryInput,
  GetMaintenancePartsInput,
  MaintenanceResponse,
  UpdateMaintenanceHistoryInput,
  UpdateMaintenancePartsInput,
} from '@cobliteam/api-lib/src/__generated__/graphql';
import {
  GraphQLMaintenanceRequester,
  type MaintenanceReminderCreateRequest,
  type MaintenanceReminderEditRequest,
  type MaintenanceReminderListRequest,
  type MaintenanceReminderResponse,
  type MaintenanceReminderRow,
  type MaintenanceReportRequest,
  NewMaintenanceRequester,
} from '@cobliteam/api-lib/src/dashboard/maintenance';

import HttpStatusCode from '@cobliteam/api-lib/src/request/HttpStatusCode';
import type { GraphQLRestDataSourceError } from '@cobliteam/api-lib/src/request/Types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';

const env = Environment.getInstance().getEnvironment();

const newMaintenanceRequester = new NewMaintenanceRequester({
  host: env.apiHost,
});

const graphQLMaintenanceRequester = new GraphQLMaintenanceRequester();

const QUERY_KEY = 'MAINTENANCE';

const getNextPageParam = (lastPage: MaintenanceResponse, allPages: MaintenanceResponse[]) => {
  const loadedItems = allPages
    .flat()
    .map((response) => response.rows?.length ?? 0)
    .reduce((acc, a) => acc + a);

  if (lastPage.count === loadedItems) return undefined;
  return allPages.flat().length;
};

const useGetMaintenanceHistory = (request: GetMaintenanceHistoryInput) => {
  return useInfiniteQuery<MaintenanceResponse>({
    initialPageParam: 0,
    queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY', JSON.stringify(request)],
    queryFn: ({ pageParam = 0 }) =>
      graphQLMaintenanceRequester.getMaintenanceHistory({
        ...request,
        page: pageParam as number,
        size: 100,
      }),
    staleTime: 1000 * 60 * 30, // 30 minutes
    getNextPageParam,
  });
};

const useGetMaintenanceParts = (request: GetMaintenancePartsInput) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
    queryFn: async () => await graphQLMaintenanceRequester.getMaintenanceParts(request),
  });
};

const useCreateMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateMaintenanceHistoryInput) => await graphQLMaintenanceRequester.createMaintenanceHistory(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      });
    },
  });
};

const useUpdateMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateMaintenanceHistoryInput) => await graphQLMaintenanceRequester.updateMaintenanceHistory(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      }),
  });
};

const useDeleteMaintenanceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteMaintenanceHistoryInput) => await graphQLMaintenanceRequester.deleteMaintenanceHistory(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_HISTORY'],
      }),
  });
};

const useCreateMaintenanceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateMaintenancePartsInput) => await graphQLMaintenanceRequester.createMaintenanceParts(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
      }),
    onError: (error: GraphQLRestDataSourceError, variables: CreateMaintenancePartsInput) => {
      if (error.status === HttpStatusCode.CONFLICT) {
        Alert.error(`Item ${variables.description} já existe.`);
        return;
      }

      Alert.error('Erro ao criar item. Tente novamente mais tarde.');
    },
  });
};

const useUpdateMaintenanceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateMaintenancePartsInput) => await graphQLMaintenanceRequester.updateMaintenanceParts(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
      }),
  });
};

const useDeleteMaintenanceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteMaintenancePartsInput) => await graphQLMaintenanceRequester.deleteMaintenanceParts(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_PARTS'],
      }),
  });
};

const useCreateMaintenanceReminder = () => {
  const queryClient = useQueryClient();

  return useMutation<MaintenanceReminderRow, unknown, MaintenanceReminderCreateRequest>({
    mutationFn: (variables) => newMaintenanceRequester.createMaintenanceReminder(variables),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      }),
  });
};

const useGetMaintenanceReport = () =>
  useMutation<Blob, unknown, { request: MaintenanceReportRequest }>({
    mutationFn: (variables) => newMaintenanceRequester.getMaintenanceReport(variables.request),
  });

const useGetMaintenanceReminder = (request: MaintenanceReminderListRequest) =>
  useQuery<MaintenanceReminderResponse>({
    queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
    queryFn: async () => await newMaintenanceRequester.getMaintenanceReminder(request),
  });

const useUpdateMaintenanceReminder = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, { id: string; request: MaintenanceReminderEditRequest }>({
    mutationFn: (variables) => newMaintenanceRequester.updateMaintenanceReminder(variables.id, variables.request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      }),
  });
};

const useDeleteMaintenanceReminder = () => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: (id) => newMaintenanceRequester.deleteMaintenanceReminder(id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, 'MAINTENANCE_REMINDER'],
      }),
  });
};

export {
  useGetMaintenanceHistory,
  useGetMaintenanceParts,
  useCreateMaintenanceHistory,
  useUpdateMaintenanceHistory,
  useDeleteMaintenanceHistory,
  useCreateMaintenanceParts,
  useUpdateMaintenanceParts,
  useDeleteMaintenanceParts,
  useGetMaintenanceReport,
  useCreateMaintenanceReminder,
  useGetMaintenanceReminder,
  useUpdateMaintenanceReminder,
  useDeleteMaintenanceReminder,
};
